import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabase";
import { useAdminMe, useStaffRole, type Group, type Org } from "../../lib/admin";
import SupportDialog from "./SupportDialog";
import { useSupportSession } from "../../ui/SupportBanner";
import Companies from "./Companies";
import Users from "./Users";
import Devices from "./Devices";
import Sims from "./Sims";
import Activity from "./Activity";
import Plans from "./Plans";
import Logs from "./Logs";
import Simulator from "./Simulator";
import { useAdminUnread } from "./Notifications";
import EmailCheck from "./EmailCheck";
import AppFooter from "../../ui/AppFooter";
import CompanyBadge from "../../ui/CompanyBadge";
import TeamPresence from "../../ui/TeamPresence";
import UserMenu from "../../ui/UserMenu";
import StatusAdmin, { statusApi } from "./StatusAdmin";
import Integrations from "./Integrations";

export default function AdminPage({ session }: { session: Session }) {
  const role = useStaffRole(session.user.id);
  const unread = useAdminUnread(role === "skyward_admin");
  const me = useAdminMe(role === "skyward_admin");
  const support = useSupportSession();
  const [supportOpen, setSupportOpen] = useState(false);
  // V3.11.0: Admin → Status (Super Admins) — notices are saved in Netlify Blobs via /api/status
  const stApi = useMemo(() => statusApi({ token: async () => (await supabase.auth.getSession()).data.session?.access_token ?? null }), []);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const reloadOrgs = useCallback(async () => {
    const { data } = await supabase.from("organisations").select("*").order("type").order("name");
    setOrgs((data ?? []) as Org[]);
  }, []);
  const [groups, setGroups] = useState<Group[]>([]);
  const reloadGroups = useCallback(async () => {
    const { data } = await supabase.from("device_groups").select("id, org_id, name").order("name");
    setGroups((data ?? []) as Group[]);
  }, []);
  useEffect(() => { if (role) { reloadOrgs(); reloadGroups(); } }, [role, reloadOrgs, reloadGroups]);

  if (role === undefined) return <div className="boot">Loading…</div>;
  if (role === null) {
    return (
      <div className="login"><div className="login-card">
        <h1>Skyward staff only</h1>
        <p className="muted">The admin area is only available to Skyward Industries staff.</p>
        <Link className="btn-primary" to="/map" style={{ textAlign: "center", textDecoration: "none" }}>Back to the map</Link>
      </div></div>
    );
  }
  const isAdmin = role === "skyward_admin";
  const canOnline = isAdmin && !!me?.support_view;   // V3.10.0: User activity (online users) — admins with Support view permission

  return (
    <div className="admin">
      <header className="topbar">
        <img src="/logo.png" alt="Tracking Fiji" className="topbar-logo" />
        <span className="beta">Admin</span>
        <div className="topbar-spacer" />
        {me?.support_view && (support
          ? <span className="support-chip" title={`Support view of ${support.org_name}`}>Support view · {support.org_name}</span>
          : <button className="btn-ghost" onClick={() => setSupportOpen(true)} title="See one client company's live data for a limited time">Support view</button>)}
        {me?.super_admin && <span className="apill info" title="Can make or remove Skyward admins and allow Support view">Super Admin</span>}
        <CompanyBadge userId={session.user.id} />
        <TeamPresence />
        <Link className="btn-ghost btn-map" to="/map">← Map</Link>
        <Link className="btn-ghost" to="/reports">Reports</Link>
        <UserMenu email={session.user.email} />
      </header>
      {supportOpen && <SupportDialog orgs={orgs} onClose={() => setSupportOpen(false)} />}
      <nav className="anav">
        {isAdmin && <NavLink to="/admin/companies">Companies</NavLink>}
        {isAdmin && <NavLink to="/admin/users">Users</NavLink>}
        {isAdmin && <NavLink to="/admin/plans">Plans</NavLink>}
        <NavLink to="/admin/devices">Devices</NavLink>
        <NavLink to="/admin/sims">SIM cards</NavLink>
        <span className="anav-sep" aria-hidden="true" />
        <NavLink to="/admin/activity">Activity{isAdmin && unread > 0 && <span className="badge">{unread > 99 ? "99+" : unread}</span>}</NavLink>
        {isAdmin && <NavLink to="/admin/email">Email</NavLink>}
        {isAdmin && <NavLink to="/admin/logs">Logs</NavLink>}
        {isAdmin && <NavLink to="/admin/simulator">Simulator</NavLink>}
        {me?.super_admin && <NavLink to="/admin/status">Status</NavLink>}
        {me?.super_admin && <NavLink to="/admin/integrations">Integrations</NavLink>}
      </nav>
      <main className="amain">
        <Routes>
          {isAdmin && <Route path="companies" element={<Companies orgs={orgs} reload={reloadOrgs} groups={groups} reloadGroups={reloadGroups} />} />}
          {isAdmin && <Route path="users" element={<Users orgs={orgs} groups={groups} superAdmin={!!me?.super_admin} />} />}
          {isAdmin && <Route path="plans" element={<Plans orgs={orgs} reloadOrgs={reloadOrgs} />} />}
          <Route path="devices" element={<Devices orgs={orgs} groups={groups} role={role} />} />
          <Route path="sims" element={<Sims />} />
          <Route path="activity" element={<Activity orgs={orgs} canOnline={canOnline} meLoaded={!isAdmin || me != null} isAdmin={isAdmin} userId={session.user.id} />} />
          <Route path="online" element={<Navigate to="/admin/activity?view=users" replace />} />   {/* V3.10.1: Online moved into Activity */}
          <Route path="notifications" element={<Navigate to="/admin/activity" replace />} />   {/* V3.10.2: Notifications moved into Activity */}
          {isAdmin && <Route path="email" element={<EmailCheck myEmail={session.user.email ?? ""} />} />}
          {isAdmin && <Route path="logs" element={<Logs />} />}
          {isAdmin && <Route path="simulator" element={<Simulator orgs={orgs} />} />}
          {isAdmin && <Route path="status" element={me == null ? <p className="muted">Loading…</p> : me.super_admin ? <StatusAdmin api={stApi} /> : <Navigate to="/admin/companies" replace />} />}
          {isAdmin && <Route path="integrations" element={me == null ? <p className="muted">Loading…</p> : me.super_admin ? <Integrations orgs={orgs} /> : <Navigate to="/admin/companies" replace />} />}   {/* V3.13.0 */}
          <Route path="*" element={<Navigate to={isAdmin ? "/admin/companies" : "/admin/devices"} replace />} />
        </Routes>
      </main>
      <AppFooter />
    </div>
  );
}
